<template>
    <main :class="embedded ? 'billing-master-editor h-full min-h-0 bg-white' : 'relative flex flex-1 flex-col overflow-y-auto bg-stage-background'" class="billing-checkbox-scope">
        <form class="flex min-h-0 flex-1 flex-col" novalidate @submit.prevent="save">
            <div v-if="embedded" class="billing-master-editor-toolbar">
                <button class="billing-master-editor-close" aria-label="Detailansicht schließen" title="Detailansicht schließen" type="button" @click="$emit('close')"><svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/></button>
                <h2 class="billing-master-editor-title">{{ isNew ? 'Eintrag anlegen' : 'Details' }}</h2>
                <button class="billing-master-editor-submit btn-stage-primary" type="submit" :disabled="loading">{{ isNew ? 'Anlegen' : 'Speichern' }}</button>
            </div>
            <div v-else class="px-8 pb-2 pt-6">
                <div class="mx-auto flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between xl:w-3/4">
                    <h1 class="stage-page-title">{{ isNew ? config.createTitle : config.editTitle }}</h1>
                    <div class="inline-flex"><router-link class="btn-stage-secondary mr-3" :to="config.basePath">Abbrechen</router-link><button class="btn-stage-primary" type="submit" :disabled="loading">Speichern</button></div>
                </div>
            </div>

            <div :class="embedded ? 'billing-master-editor-body' : 'stage-page-body'">
                <div :class="embedded ? 'billing-master-inline-sections' : 'mx-auto w-full space-y-6 xl:w-3/4'">
                    <loading :status="loading"/>
                    <div v-if="embedded && detailHolder" class="billing-master-editor-profile">
                        <img
                            :alt="detailHolder.name || 'Inhaber'"
                            :class="detailHolder.type === 'user' ? 'rounded-full' : 'rounded-sm'"
                            :src="detailHolder.image || fallbackImage"
                            class="billing-master-editor-profile-image"
                        >
                        <div class="min-w-0 flex-1">
                            <h2 class="billing-master-editor-profile-title truncate">{{ detailHolder.name }}</h2>
                            <p class="billing-master-editor-profile-subtitle truncate">{{ detailSubtitle }}</p>
                        </div>
                    </div>
                    <section class="billing-master-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Stammdaten</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                            <template v-if="isNew">
                                <div><label class="stage-filter-label mb-2 block">Art des Inhabers</label><input-select v-model="form.holder_type" class="stage-filter-select" :options="holderTypeOptions" option-label="name" required @change="holderTypeChanged"/></div>
                                <div><label class="stage-filter-label mb-2 block">Inhaber</label><input-select v-model="form.holder_id" class="stage-filter-select" :options="eligibleHolders" option-label="display_label" searchable required><template v-slot:selectedOption="props"><div class="flex min-w-0 items-center gap-3"><img :src="props.option.image || fallbackImage" :class="form.holder_type === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-6 w-6 flex-shrink-0 object-cover"><span class="truncate">{{ props.option.display_label }}</span></div></template><template v-slot:selectOption="props"><div class="flex min-w-0 items-center gap-3"><img :src="props.option.image || fallbackImage" :class="form.holder_type === 'user' ? 'rounded-full' : 'rounded-sm'" class="h-7 w-7 flex-shrink-0 object-cover"><span class="truncate text-sm text-gray-800">{{ props.option.display_label }}</span></div></template></input-select></div>
                            </template>
                            <div class="md:col-span-2"><label class="stage-filter-label mb-2 block" for="master-label">Bezeichnung</label><input id="master-label" v-model.trim="form.label" class="stage-filter-input form-input block w-full text-sm sm:leading-5" maxlength="255" required :placeholder="config.labelPlaceholder"></div>
                            <div>
                                <label class="inline-flex items-center gap-2 text-sm text-gray-700"><input v-model="form.is_default" :disabled="!isNew && item.is_default" class="form-checkbox h-4 w-4" type="checkbox">Standard für diesen Inhaber</label>
                            </div>
                            <div>
                                <label class="inline-flex items-center gap-2 text-sm text-gray-700"><input v-model="form.is_active" :disabled="!isNew && item.service_accounts_count > 0" class="form-checkbox h-4 w-4" type="checkbox">Datensatz aktiv</label>
                            </div>
                            <p v-if="!isNew && item.service_accounts_count > 0" class="text-xs text-gray-500 md:col-span-2">Verwendete Stammdaten können nicht deaktiviert werden.</p>
                        </div>
                    </section>

                    <section v-if="kind === 'profile'" class="billing-master-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Rechnungsanschrift</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-6">
                            <div class="md:col-span-6"><label class="stage-filter-label mb-2 block" for="profile-recipient">Rechnungsempfänger</label><input id="profile-recipient" v-model.trim="form.recipient_name" class="stage-filter-input form-input block w-full text-sm sm:leading-5" required></div>
                            <div class="md:col-span-5"><label class="stage-filter-label mb-2 block" for="profile-street">Straße</label><input id="profile-street" v-model.trim="form.street" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div class="md:col-span-1"><label class="stage-filter-label mb-2 block" for="profile-number">Nr.</label><input id="profile-number" v-model.trim="form.house_number" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div class="md:col-span-2"><label class="stage-filter-label mb-2 block" for="profile-postal">PLZ</label><input id="profile-postal" v-model.trim="form.postal_code" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div class="md:col-span-4"><label class="stage-filter-label mb-2 block" for="profile-city">Ort</label><input id="profile-city" v-model.trim="form.city" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div class="md:col-span-3"><label class="stage-filter-label mb-2 block" for="profile-country">Land</label><input id="profile-country" v-model.trim="form.country" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div class="md:col-span-3"><label class="stage-filter-label mb-2 block" for="profile-email">E-Mail</label><input id="profile-email" v-model.trim="form.billing_email" class="stage-filter-input form-input block w-full text-sm sm:leading-5" type="email"></div>
                        </div>
                    </section>

                    <section v-if="kind === 'profile'" class="billing-master-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Steuern</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                            <div><label class="stage-filter-label mb-2 block" for="profile-tax">Steuernummer</label><input id="profile-tax" v-model.trim="form.tax_number" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div><label class="stage-filter-label mb-2 block" for="profile-vat">USt-ID</label><input id="profile-vat" v-model.trim="form.vat_id" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                        </div>
                    </section>

                    <section v-else class="billing-master-form-section overflow-hidden bg-white px-6 pb-6 shadow sm:rounded">
                        <h3 class="-mx-6 flex h-16 items-center border-b border-ultralight stage-header-surface px-6 text-base font-semibold leading-6 text-gray-900">Bankverbindung</h3>
                        <div class="mt-5 grid grid-cols-1 gap-x-5 gap-y-4 md:grid-cols-2">
                            <div><label class="stage-filter-label mb-2 block" for="bank-holder">Kontoinhaber</label><input id="bank-holder" v-model.trim="form.account_holder" class="stage-filter-input form-input block w-full text-sm sm:leading-5" required></div>
                            <div><label class="stage-filter-label mb-2 block" for="bank-name">Kreditinstitut</label><input id="bank-name" v-model.trim="form.bank_name" class="stage-filter-input form-input block w-full text-sm sm:leading-5"></div>
                            <div><label class="stage-filter-label mb-2 block" for="bank-iban">IBAN</label><input id="bank-iban" v-model.trim="form.iban" autocomplete="off" autocapitalize="characters" class="stage-filter-input form-input block w-full text-sm uppercase sm:leading-5" maxlength="42" required spellcheck="false"></div>
                            <div><label class="stage-filter-label mb-2 block" for="bank-bic">BIC</label><input id="bank-bic" v-model.trim="form.bic" autocomplete="off" autocapitalize="characters" class="stage-filter-input form-input block w-full text-sm uppercase sm:leading-5" maxlength="14" spellcheck="false"></div>
                            <div><label class="stage-filter-label mb-2 block" for="bank-currency">Währung</label><input id="bank-currency" v-model.trim="form.currency" class="stage-filter-input form-input block w-full text-sm uppercase sm:leading-5" maxlength="3" required></div>
                        </div>
                    </section>

                </div>
            </div>
            <footer v-if="embedded || !isNew" class="billing-master-editor-footer">
                <span v-if="lastUpdatedAt" class="billing-master-editor-updated text-xs text-gray-500">
                    <img
                        v-if="lastUpdatedUser && lastUpdatedUser.image"
                        :alt="lastUpdatedUser.name"
                        :src="lastUpdatedUser.image"
                        class="billing-master-editor-updated-avatar"
                    >
                    <span v-if="lastUpdatedUser">{{ lastUpdatedUser.name }}</span>
                    <span>{{ lastUpdatedUser ? 'am ' : '' }}{{ lastUpdatedAt }}</span>
                </span>
                <button v-if="!isNew" class="billing-master-editor-delete btn-stage-icon-plain" :aria-label="config.deleteTitle" :title="config.deleteTitle" type="button" @click="deleteDialogOpen = true">
                    <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"/>
                </button>
            </footer>
        </form>
        <billing-delete-dialog
            :blockers="deleteGuard.blockers"
            :deletable="canDelete"
            :loading="loading"
            :open="deleteDialogOpen"
            :confirmation-text="deleteConfirmationText"
            :title="config.deleteTitle"
            @close="deleteDialogOpen = false"
            @confirm="remove"
        />
    </main>
</template>

<script>
import BillingDeleteDialog from './delete-dialog';

const emptyForm = () => ({holder_type: 'company', holder_id: null, label: '', recipient_name: '', billing_email: '', street: '', house_number: '', postal_code: '', city: '', country: 'Deutschland', tax_number: '', vat_id: '', account_holder: '', bank_name: '', iban: '', bic: '', currency: 'EUR', is_default: false, is_active: true});

export default {
    name: 'workspace-billing-master-data-form',
    components: {BillingDeleteDialog},
    props: {
        kind: {type: String, required: true},
        embedded: {type: Boolean, default: false},
        recordId: {type: [Number, String], default: null},
    },
    data() { return {loading: false, item: {}, options: {companies: [], private_customers: []}, form: emptyForm(), holderTypeOptions: [{id: 'company', name: 'Unternehmen'}, {id: 'user', name: 'Privatkunde'}], fallbackImage: '/images/default/icon.png', deleteDialogOpen: false}; },
    computed: {
        config() { return this.kind === 'profile' ? {endpoint: 'billing-profiles', basePath: '/workspace/billing-profiles', createTitle: 'Rechnungsanschrift anlegen', editTitle: 'Rechnungsanschrift bearbeiten', deleteTitle: 'Rechnungsanschrift löschen', labelPlaceholder: 'z. B. Hauptsitz'} : {endpoint: 'bank-accounts', basePath: '/workspace/bank-accounts', createTitle: 'Bankverbindung anlegen', editTitle: 'Bankverbindung bearbeiten', deleteTitle: 'Bankverbindung löschen', labelPlaceholder: 'z. B. Geschäftskonto'}; },
        isNew() { return this.embedded ? !this.recordId : !this.$route.params.id; },
        resolvedRecordId() { return this.embedded ? this.recordId : this.$route.params.id; },
        eligibleHolders() { const source = this.form.holder_type === 'user' ? this.options.private_customers : this.options.companies; return (source || []).map((holder) => ({...holder, display_label: holder.name + (holder.email ? ' · ' + holder.email : '')})); },
        detailHolder() {
            if (!this.isNew && this.item.id) return {name: this.item.holder_name, image: this.item.holder_image, type: this.item.holder_type};
            const holder = this.eligibleHolders.find((entry) => Number(entry.id) === Number(this.form.holder_id));
            return holder ? {name: holder.name, image: holder.image, type: this.form.holder_type} : null;
        },
        detailSubtitle() { return String(this.form.label || (this.kind === 'profile' ? 'Rechnungsanschrift' : 'Bankverbindung')).trim(); },
        deleteGuard() { return this.item.delete_guard || {deletable: false, blockers: []}; },
        canDelete() { return this.deleteGuard.deletable === true; },
        deleteConfirmationText() { return 'Möchten Sie diese ' + (this.kind === 'profile' ? 'Rechnungsanschrift' : 'Bankverbindung') + ' wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.'; },
        lastUpdatedAt() {
            if (this.isNew || !this.item.updated_at) return '';
            return this.$momentFormatDateTime(this.item.updated_at).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        lastUpdatedUser() { return this.item.updated_by_user || null; },
    },
    watch: {
        kind() { this.deleteDialogOpen = false; this.item = {}; this.form = emptyForm(); this.load(); },
        recordId() { if (this.embedded) { this.deleteDialogOpen = false; this.item = {}; this.form = emptyForm(); this.load(); } },
    },
    mounted() { this.load(); },
    methods: {
        load() { this.loading = true; const url = 'api/workspace/admin/' + this.config.endpoint + (this.isNew ? '' : '/' + this.resolvedRecordId); axios.get(url).then((response) => { this.options = response.data.options || this.options; if (!this.isNew) { this.item = response.data.item || {}; this.form = {...emptyForm(), ...this.item}; } }).finally(() => { this.loading = false; }); },
        save() { this.loading = true; const creating = this.isNew; const base = {label: this.form.label, is_default: !!this.form.is_default, is_active: !!this.form.is_active}; const details = this.kind === 'profile' ? {recipient_name: this.form.recipient_name, billing_email: this.form.billing_email || null, street: this.form.street || null, house_number: this.form.house_number || null, postal_code: this.form.postal_code || null, city: this.form.city || null, country: this.form.country || null, tax_number: this.form.tax_number || null, vat_id: this.form.vat_id || null} : {account_holder: this.form.account_holder, bank_name: this.form.bank_name || null, iban: this.form.iban, bic: this.form.bic || null, currency: this.form.currency}; const holder = creating ? {holder_type: this.form.holder_type, holder_id: Number(this.form.holder_id)} : {}; const payload = {...base, ...details, ...holder}; const request = creating ? axios.post('api/workspace/admin/' + this.config.endpoint, payload) : axios.put('api/workspace/admin/' + this.config.endpoint + '/' + this.item.id, payload); request.then((response) => { this.$notify({title: 'Erfolg', text: response.data.message || 'Daten gespeichert.', type: 'success'}); const savedItem = response.data.item || this.item; const id = savedItem && savedItem.id; this.item = savedItem; this.form = {...emptyForm(), ...this.item}; this.$emit('saved', savedItem); if (!this.embedded && creating && id) this.$router.replace(this.config.basePath + '/' + id + '/edit'); }).finally(() => { this.loading = false; }); },
        holderTypeChanged() { this.form.holder_id = null; },
        remove() { if (!this.canDelete || this.loading) return; this.loading = true; const removedId = this.item.id; axios.delete('api/workspace/admin/' + this.config.endpoint + '/' + removedId).then(() => { this.deleteDialogOpen = false; this.$emit('deleted', removedId); if (!this.embedded) this.$router.push(this.config.basePath); }).catch((error) => { const guard = error.response && error.response.status === 422 && error.response.data && error.response.data.delete_guard; if (guard) this.item = {...this.item, delete_guard: guard}; }).finally(() => { this.loading = false; }); },
    },
};
</script>

<style scoped>
.billing-master-editor {
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.billing-master-editor-toolbar {
    display: flex;
    height: 3.25rem;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    padding: 0 0 0 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.billing-master-editor-submit {
    align-self: stretch;
    min-width: 8rem;
    margin-left: auto;
    border: 0;
    border-radius: 0;
}

.billing-master-editor-title {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
    overflow: hidden;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.billing-master-editor-close {
    display: none;
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    color: var(--ui-text-subtle);
}

.billing-master-editor-body {
    position: relative;
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 1.5rem 1.75rem 2rem;
    background: #fff;
}

.billing-master-editor-profile {
    display: flex;
    min-height: 4.75rem;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;
}

.billing-master-editor-profile-image {
    width: 4.5rem;
    height: 4.5rem;
    flex: 0 0 4.5rem;
    object-fit: cover;
}

.billing-master-editor-profile-title {
    color: var(--ui-text-strong);
    font-size: 1.25rem;
    font-weight: 600;
    line-height: 1.25;
}

.billing-master-editor-profile-subtitle {
    margin-top: 0.125rem;
    color: var(--ui-text-disabled);
    font-size: 0.875rem;
    line-height: 1rem;
}

.billing-master-editor-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0 1.5rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.billing-master-editor-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.billing-master-editor-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    border-radius: 9999px;
    object-fit: cover;
}

.billing-master-editor-delete {
    color: var(--ui-text-subtle);
}

.billing-master-editor-delete:hover,
.billing-master-editor-delete:focus {
    color: var(--ui-text-default);
}

.billing-master-editor .billing-master-form-section {
    overflow: visible;
    padding: 0;
    border: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
}

.billing-master-editor .billing-master-form-section + .billing-master-form-section {
    margin-top: 1.75rem;
}

.billing-master-editor .billing-master-form-section > h3 {
    display: block;
    height: auto;
    margin: 0 0 0.75rem;
    padding: 0;
    border: 0;
    background: transparent;
    color: var(--ui-text-strong);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.billing-master-editor .billing-master-form-section > h3 + div {
    margin-top: 0;
}

@media (max-width: 1279px) {
    .billing-master-editor-close {
        display: inline-flex;
    }
}

@media (max-width: 639px) {
    .billing-master-editor-toolbar {
        height: 3.25rem;
        min-height: 3.25rem;
        padding-left: 0.75rem;
    }

    .billing-master-editor-body {
        padding: 0.75rem;
    }
}
</style>
